import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { ShieldCheck, Loader2, Pencil, ScrollText, LayoutGrid, Table2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const emptyContract = () => ({
  valid_dimensions: [], grains: [], min_population: 10,
  required_data_quality_state: '', jurisdictions: '', date_basis: [], permitted_use: [],
});

// A stored contract may carry legacy single-string permitted_use / date_basis;
// coerce either to the array the editor now expects.
const toArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);

export default function ApplicabilityContracts() {
  const canWrite = useCan('measures:certify');
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [measures, setMeasures] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [dates, setDates] = useState([]);
  const [options, setOptions] = useState({ behavior_classes: [], grains: [], permitted_use: [], data_quality_states: [] });
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [contract, setContract] = useState(emptyContract());
  const [behaviorClass, setBehaviorClass] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getSemanticModels().then((r) => {
      const ms = r.models || r || [];
      setModels(ms);
      if (ms.length && !modelId) setModelId(ms[0].model_id);
    }).catch(() => {});
    api.getApplicabilityOptions().then(setOptions).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMeasures = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const r = await api.getApplicabilityMeasures(modelId);
      setMeasures(r.measures || []);
      setDimensions(r.dimensions || []);
      setDates(r.dates || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load measures');
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => { loadMeasures(); }, [loadMeasures]);

  const openEdit = (m) => {
    setEditing(m);
    const c = m.applicability || {};
    setContract({
      valid_dimensions: c.valid_dimensions || [],
      grains: c.grains || [],
      min_population: c.min_population ?? 10,
      required_data_quality_state: c.required_data_quality_state || '',
      jurisdictions: (c.jurisdictions || []).join(', '),
      date_basis: toArr(c.date_basis),
      permitted_use: toArr(c.permitted_use),
    });
    setBehaviorClass(m.behavior_class || '');
    setDialogOpen(true);
  };

  const toggleIn = (key, value) => {
    setContract((p) => {
      const set = new Set(p[key]);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...p, [key]: Array.from(set) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.saveApplicability(editing.measure_id, {
        applicability: {
          ...contract,
          jurisdictions: contract.jurisdictions.split(',').map((s) => s.trim()).filter(Boolean),
        },
        behavior_class: behaviorClass,
      });
      toast.success(`Saved contract for "${editing.label}"`);
      setDialogOpen(false);
      loadMeasures();
    } catch (e) {
      toast.error(e.message || 'Failed to save contract');
    } finally {
      setSaving(false);
    }
  };

  const contractSummary = (m) => {
    const c = m.applicability || {};
    const bits = [];
    if (m.behavior_class) bits.push(m.behavior_class.replace(/_/g, ' '));
    if ((c.valid_dimensions || []).length) bits.push(`${c.valid_dimensions.length} dim${c.valid_dimensions.length === 1 ? '' : 's'}`);
    if ((c.grains || []).length) bits.push(c.grains.join('/'));
    if (c.min_population) bits.push(`min ${c.min_population}`);
    if (c.required_data_quality_state) bits.push(`needs ${c.required_data_quality_state}`);
    if (toArr(c.permitted_use).length) bits.push(toArr(c.permitted_use).join('/'));
    if (toArr(c.date_basis).length) bits.push(`${toArr(c.date_basis).length} date${toArr(c.date_basis).length === 1 ? '' : 's'}`);
    return bits.length ? bits.join(' · ') : 'No contract set';
  };

  // One-line "what downgrades" hint derived from a measure's stored contract —
  // the card/table-row surface of the dialog's enforcement preview.
  const downgradeHint = (m) => {
    const c = m.applicability || {};
    const bits = [];
    if ((c.valid_dimensions || []).length) bits.push(`breakdowns outside ${c.valid_dimensions.length} validated dim${c.valid_dimensions.length === 1 ? '' : 's'}`);
    if ((c.grains || []).length) bits.push(`grains other than ${c.grains.join('/')}`);
    if (c.required_data_quality_state) bits.push(`unconfirmed "${c.required_data_quality_state}"`);
    return bits.length ? `Downgrades: ${bits.join(', ')}` : 'No auto-downgrade rules';
  };

  // Dimensions grouped by their source table for the editor.
  const dimGroups = (() => {
    const g = {};
    dimensions.forEach((d) => { (g[d.table || 'Other'] = g[d.table || 'Other'] || []).push(d); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  })();

  // Contract Enforcement Preview — a LIVE example of what would auto-downgrade
  // an answer to Provisional under the contract as currently edited. Mirrors the
  // backend's evaluate_applicability (dimensions, time grains, data-quality state
  // are the auto-enforced rules).
  const preview = (() => {
    const downgrades = [];
    const allowedDims = contract.valid_dimensions || [];
    if (allowedDims.length) {
      const unallowed = dimensions.find((d) => !allowedDims.includes(d.name));
      if (unallowed) downgrades.push(`A breakdown by "${unallowed.label}" (only the ${allowedDims.length} chosen dimension${allowedDims.length === 1 ? '' : 's'} ${allowedDims.length === 1 ? 'is' : 'are'} validated)`);
    }
    const allowedGrains = contract.grains || [];
    if (allowedGrains.length) {
      const unGrain = (options.grains || []).find((g) => !allowedGrains.includes(g));
      if (unGrain) downgrades.push(`A ${unGrain}-grain query (validated at ${allowedGrains.join('/')} only)`);
    }
    if (contract.required_data_quality_state) {
      downgrades.push(`Any answer where the source isn't confirmed "${contract.required_data_quality_state}"`);
    }
    const passes = [];
    if (allowedDims.length) passes.push(`breakdowns by ${allowedDims.slice(0, 3).map((n) => (dimensions.find((d) => d.name === n) || {}).label || n).join(', ')}${allowedDims.length > 3 ? '…' : ''}`);
    else passes.push('any breakdown dimension');
    passes.push(allowedGrains.length ? `${allowedGrains.join('/')} grain` : 'any time grain');
    return { downgrades, passes, minPop: contract.min_population };
  })();

  return (
    <div data-testid="applicability-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Label className="text-sm text-muted-foreground">Model</Label>
        <select
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          className="h-9 rounded-md border border-border/50 bg-background px-3 text-sm"
          data-testid="applicability-model-select"
        >
          {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
        </select>
        <div className="ml-auto inline-flex rounded-md border border-border/50 overflow-hidden" data-testid="applicability-view-toggle">
          <button onClick={() => setViewMode('cards')}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs ${viewMode === 'cards' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/40'}`}
            data-testid="applicability-view-cards">
            <LayoutGrid className="w-3.5 h-3.5" /> Cards
          </button>
          <button onClick={() => setViewMode('table')}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border-l border-border/50 ${viewMode === 'table' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/40'}`}
            data-testid="applicability-view-table">
            <Table2 className="w-3.5 h-3.5" /> Table
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : measures.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="applicability-empty">
          No certified measures in this model yet. Certify a measure first, then set the scope it's validated for.
        </div>
      ) : viewMode === 'table' ? (
        <div className="overflow-x-auto rounded-2xl border border-border/40" data-testid="applicability-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/40 bg-muted/30 text-left text-muted-foreground">
                <th className="px-3 py-2 font-medium">Measure</th>
                <th className="px-3 py-2 font-medium">Behavior</th>
                <th className="px-3 py-2 font-medium">Contract</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {measures.map((m) => (
                <tr key={m.measure_id} className="border-b border-border/20 hover:bg-muted/20" data-testid={`applicability-row-${m.measure_id}`}>
                  <td className="px-3 py-2 font-medium text-foreground flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> {m.label}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground capitalize">{m.behavior_class ? m.behavior_class.replace(/_/g, ' ') : '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    <div>{contractSummary(m)}</div>
                    <div className="text-[10px] mt-0.5 flex items-center gap-1 text-amber-600/90 dark:text-amber-400/90" data-testid={`downgrade-hint-${m.measure_id}`}>
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {downgradeHint(m)}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canWrite && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(m)} data-testid={`edit-contract-${m.measure_id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="applicability-list">
          {measures.map((m) => (
            <div key={m.measure_id} className="bg-card/60 border border-border/40 rounded-2xl p-4"
              data-testid={`applicability-card-${m.measure_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-heading font-semibold text-foreground flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" /> {m.label}
                </div>
                {canWrite && (
                  <Button size="sm" variant="outline" onClick={() => openEdit(m)}
                    data-testid={`edit-contract-${m.measure_id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit contract
                  </Button>
                )}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <ScrollText className="w-3.5 h-3.5" /> {contractSummary(m)}
              </div>
              <div className="text-[11px] mt-1 flex items-start gap-1.5 text-amber-600/90 dark:text-amber-400/90" data-testid={`downgrade-hint-${m.measure_id}`}>
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {downgradeHint(m)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="applicability-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" /> Applicability contract — {editing?.label}
            </DialogTitle>
            <DialogDescription>
              Define the scope this certified measure is validated for, within <span className="text-foreground font-medium">{models.find((m) => m.model_id === modelId)?.name || 'this model'}</span>. Used outside it, an answer auto-downgrades to Provisional with a visible reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Behavior class</Label>
              <select
                value={behaviorClass}
                onChange={(e) => setBehaviorClass(e.target.value)}
                className="h-9 w-full rounded-md border border-border/50 bg-background px-3 text-sm mt-1"
                data-testid="behavior-class-select"
              >
                <option value="">Not set</option>
                {(options.behavior_classes || []).map((b) => (
                  <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Valid breakdown dimensions</Label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Grouped by source table. Leave all unchecked to allow any dimension.</p>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="valid-dimensions">
                {dimGroups.map(([table, items]) => (
                  <div key={table} data-testid={`dim-group-${table}`}>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80 font-semibold mb-1">{table}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((d) => {
                        const on = contract.valid_dimensions.includes(d.name);
                        return (
                          <button key={d.name} onClick={() => toggleIn('valid_dimensions', d.name)}
                            className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${on ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border/40 text-muted-foreground hover:bg-muted/40'}`}
                            data-testid={`dim-chip-${d.name}`}>
                            {d.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {dimensions.length === 0 && <span className="text-[11px] text-muted-foreground">No dimensions in this model.</span>}
              </div>
            </div>
            <div>
              <Label>Allowed time grains</Label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="grains">
                {(options.grains || []).map((g) => {
                  const on = contract.grains.includes(g);
                  return (
                    <button key={g} onClick={() => toggleIn('grains', g)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border/40 text-muted-foreground hover:bg-muted/40'}`}
                      data-testid={`grain-chip-${g}`}>
                      {g}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Permitted use</Label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Select all that apply.</p>
              <div className="flex flex-wrap gap-1.5" data-testid="permitted-use">
                {(options.permitted_use || []).map((u) => {
                  const on = contract.permitted_use.includes(u);
                  return (
                    <button key={u} onClick={() => toggleIn('permitted_use', u)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors capitalize ${on ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border/40 text-muted-foreground hover:bg-muted/40'}`}
                      data-testid={`permitted-use-chip-${u}`}>
                      {u}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Minimum population</Label>
                <Input type="number" min={0} value={contract.min_population}
                  onChange={(e) => setContract((p) => ({ ...p, min_population: e.target.value }))}
                  data-testid="min-population-input" />
              </div>
              <div>
                <Label>Required data-quality state</Label>
                <select value={contract.required_data_quality_state}
                  onChange={(e) => setContract((p) => ({ ...p, required_data_quality_state: e.target.value }))}
                  className="h-9 w-full rounded-md border border-border/50 bg-background px-3 text-sm mt-1"
                  data-testid="dq-state-select">
                  <option value="">Not required</option>
                  {(options.data_quality_states || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Date basis</Label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Business date field(s) this measure is validated against. Leave empty for any.</p>
              <div className="flex flex-wrap gap-1.5" data-testid="date-basis">
                {dates.map((d) => {
                  const on = contract.date_basis.includes(d.name);
                  return (
                    <button key={d.name} onClick={() => toggleIn('date_basis', d.name)}
                      className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${on ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border/40 text-muted-foreground hover:bg-muted/40'}`}
                      data-testid={`date-basis-chip-${d.name}`}>
                      {d.label} <span className="opacity-50">· {d.table}</span>
                    </button>
                  );
                })}
                {dates.length === 0 && <span className="text-[11px] text-muted-foreground">No date fields in this model.</span>}
              </div>
            </div>
            <div>
              <Label>Jurisdictions (comma-separated)</Label>
              <Input value={contract.jurisdictions}
                onChange={(e) => setContract((p) => ({ ...p, jurisdictions: e.target.value }))}
                placeholder="e.g. US-CA, US-NY" data-testid="jurisdictions-input" />
            </div>

            {/* Contract Enforcement Preview — live example of what downgrades to Provisional */}
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/[0.05] p-3" data-testid="contract-preview">
              <p className="text-[11px] font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-1.5 mb-1.5">
                <ScrollText className="w-3.5 h-3.5" /> Enforcement preview — what this contract would do to a live answer
              </p>
              {preview.downgrades.length > 0 ? (
                <div className="space-y-1" data-testid="contract-preview-downgrades">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Auto-downgrades to Provisional</p>
                  {preview.downgrades.map((d, i) => (
                    <p key={i} className="text-[11px] text-amber-700 dark:text-amber-300 flex items-start gap-1.5" data-testid={`contract-preview-downgrade-${i}`}>
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {d}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground" data-testid="contract-preview-none">
                  This contract adds no auto-downgrade rules — answers keep their source trust level. Pick dimensions, grains, or a data-quality state above to scope it.
                </p>
              )}
              <p className="text-[10px] text-emerald-700 dark:text-emerald-300 mt-1.5 flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" /> Passes cleanly: {preview.passes.join(' · ')}{preview.minPop ? ` · min population ${preview.minPop}` : ''}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-contract-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Save contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { ApplicabilityContracts };
